/**
 * Qué pasos del wizard recorre cada usuario (Sprint 21-10).
 *
 * Vive aparte de `Onboarding.tsx` porque exportar una función desde un archivo
 * de componentes rompe el fast refresh de Vite — y porque así se puede probar
 * sin montar router, contexto municipal ni cliente de query.
 */

export type Step = "welcome" | "municipality" | "profile" | "confirm";

/**
 * Quien llega por invitación **ya tiene municipio**: se lo asignó el admin al
 * invitarlo.
 *
 * `determineStartStep` ya lo mandaba directo a `profile`, pero la barra de
 * progreso seguía dibujando los cuatro pasos — así que el invitado aterrizaba
 * en "3 de 4" sin haber hecho nada, como si hubiera perdido progreso.
 *
 * Pedirle que elija municipio tampoco es opción: ya es de su alcaldía, y
 * ofrecerle la lista abre la puerta a que elija otra.
 *
 * La bienvenida no es un paso de la barra (issue #624): dice "En 3 pasos" y
 * lista municipio, perfil y términos, así que la barra dibuja esos tres. En
 * la bienvenida ningún punto está activo todavía.
 */
export function stepsForUser(hasTenant: boolean): Step[] {
  return hasTenant ? ["profile", "confirm"] : ["municipality", "profile", "confirm"];
}

export interface StartStepContext {
  has_tenant: boolean;
  full_name: string | null;
  onboarded_at: string | null;
}

/**
 * Un nombre "real" es el que la persona escribió: ni vacío, ni su email,
 * ni el placeholder `Usuario …`.
 *
 * 7-sep-2026: `""` — lo que deja `invitar_miembro` en `users.full_name` al
 * invitar — pasaba como nombre real, y el invitado aterrizaba en "confirmar"
 * sin haber llenado su perfil.
 */
export function hasRealName(fullName: string | null): boolean {
  if (fullName === null) return false;
  const nombre = fullName.trim();
  return nombre !== "" && !nombre.includes("@") && !nombre.startsWith("Usuario ");
}

/**
 * Desde qué paso arranca el wizard según lo que el usuario ya tiene.
 *
 * - Sin municipio: la bienvenida, que le explica los tres pasos que vienen.
 * - Con municipio (invitado) pero sin nombre real: directo a su perfil.
 * - Con nombre pero sin terminar: solo le falta confirmar.
 *
 * Quien ya terminó también cae en "confirm", pero nunca lo ve: `Onboarding`
 * lo redirige al radar.
 */
export function determineStartStep(ctx: StartStepContext): Step {
  if (!ctx.has_tenant) return "welcome";
  if (!hasRealName(ctx.full_name)) return "profile";
  return "confirm";
}
